import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { ChatMessage, ProposalState } from '../../models/clinicAgent';
import { isApplicable, groupProposalsByDay, ProposalUnit } from '../../services/clinicAgentProposals';
import ProposalCard from './ProposalCard';
import ProposalDayGroupCard from './ProposalDayGroupCard';
import BulkProposalActions from './BulkProposalActions';

interface MessageListProps {
  messages: ChatMessage[];
  typing: boolean;
  statusLabel: string | null;
  onResolveProposal: (proposalId: string, decision: 'accept' | 'reject') => void;
  onResolveManyProposals: (proposalIds: string[], decision: 'accept' | 'reject') => void;
  onRetrySend: (messageId: string) => void;
  hasMoreHistory: boolean;
  loadingMoreHistory: boolean;
  onLoadMoreHistory: () => void;
}

// Distance from the top (px) at which scrolling up triggers loading older pages.
const LOAD_MORE_THRESHOLD = 60;

// A bulk bar needs at least this many separate render units (cards/groups) to
// be worth drawing — within a single day-group the group card has its own
// accept/discard-all, so a lone unit never gets a cross-card bar.
const BULK_MIN_UNITS = 2;

// One thing to render: an ordinary message (text/image/notice) shown as-is, or a
// run of proposal cards gathered from consecutive proposal rows. Proposals are
// persisted one row per day, so the same item across several days arrives as
// several adjacent "proposal" messages — we pull them out of the message flow and
// fold them (via groupProposalsByDay) into day-groups in a single block.
type RenderBlock =
  | { kind: 'message'; key: string; message: ChatMessage }
  | { kind: 'proposals'; key: string; units: ProposalUnit[] };

function buildRenderPlan(messages: ChatMessage[]): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  let buffer: ProposalState[] = [];

  const flushProposals = () => {
    if (buffer.length === 0) return;
    const units = groupProposalsByDay(buffer);
    blocks.push({ kind: 'proposals', key: `props-${units[0].key}`, units });
    buffer = [];
  };

  for (const m of messages) {
    // Any visible message content (a bubble, an image, a notice) ends the current
    // run of proposals and renders in place.
    const hasContent = m.messageType === 'system_notice' || m.text.trim() !== '' || !!m.fileUrl;
    if (hasContent) {
      flushProposals();
      blocks.push({ kind: 'message', key: m.id, message: m });
    }
    // Proposal rows (empty bubble) accumulate so adjacent days fold together,
    // even when they arrive as separate chat rows.
    if (m.proposals && m.proposals.length > 0) {
      buffer.push(...m.proposals);
    }
  }
  flushProposals();
  return blocks;
}

// Within one proposals block, find runs of consecutive units that still hold
// untouched (pending + applicable) proposals. Returns, keyed by the run's last
// unit ("<blockKey>:<unitIndex>"), the flat list of proposal_ids in that run, so
// a single "accept/discard all" bar can be drawn below it. A resolved or
// non-applicable unit breaks the run.
function computeBulkGroups(blocks: RenderBlock[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const block of blocks) {
    if (block.kind !== 'proposals') continue;
    let run: { key: string; ids: string[] }[] = [];
    const flush = () => {
      if (run.length >= BULK_MIN_UNITS) {
        groups.set(run[run.length - 1].key, run.flatMap((r) => r.ids));
      }
      run = [];
    };
    block.units.forEach((unit, ui) => {
      const action = unit.proposals[0].action;
      const ids = isApplicable(action)
        ? unit.proposals.filter((p) => p.id && p.status === 'pending').map((p) => p.id as string)
        : [];
      if (ids.length > 0) {
        run.push({ key: `${block.key}:${ui}`, ids });
      } else {
        flush();
      }
    });
    flush();
  }
  return groups;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  typing,
  statusLabel,
  onResolveProposal,
  onResolveManyProposals,
  onRetrySend,
  hasMoreHistory,
  loadingMoreHistory,
  onLoadMoreHistory,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(messages.length);
  // Id of the last message while it was still streaming, so we notice when its
  // final version lands in place (same id, count unchanged, typing just ended).
  const streamingLastIdRef = useRef<string | null>(null);
  // scrollHeight captured just before an older page is requested; non-null marks
  // a pending prepend whose scroll position must be restored once it lands.
  const pendingPrependHeightRef = useRef<number | null>(null);
  const justPrependedRef = useRef(false);
  // Whether we've already jumped to the bottom for the first batch of messages.
  const didInitialScrollRef = useRef(false);

  // Trigger a load when the clinician scrolls near the top. The captured height
  // ref also acts as an in-flight guard, so rapid scroll events fire once.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (
      el.scrollTop <= LOAD_MORE_THRESHOLD &&
      hasMoreHistory &&
      !loadingMoreHistory &&
      pendingPrependHeightRef.current == null
    ) {
      pendingPrependHeightRef.current = el.scrollHeight;
      onLoadMoreHistory();
    }
  }, [hasMoreHistory, loadingMoreHistory, onLoadMoreHistory]);

  // After older messages prepend, keep the viewport anchored on the message the
  // clinician was looking at by offsetting scrollTop by the added height. Runs
  // before paint (and before the bottom-scroll effect below) to avoid a flash.
  const blocks = useMemo(() => buildRenderPlan(messages), [messages]);
  const bulkGroups = useMemo(() => computeBulkGroups(blocks), [blocks]);
  // While a streamed reply is visible it is the progress indicator; the chip
  // only comes back when a status arrives (the agent moved on to tools).
  const lastMessage = messages[messages.length - 1];
  const streamingVisible = !!lastMessage?.streaming && lastMessage.text.trim() !== '';

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && pendingPrependHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - pendingPrependHeightRef.current;
      pendingPrependHeightRef.current = null;
      justPrependedRef.current = true;
    }
  }, [messages]);

  useLayoutEffect(() => {
    // Only auto-scroll when a new message arrives or the agent is working —
    // not when an existing message mutates (e.g. confirming/discarding a
    // proposal), so the clinician keeps focus on the proposal they're acting on.
    const grew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;
    // The final reply replaces the streamed bubble instead of appending, often
    // with proposal cards below it: treat that like a new message arriving.
    const last = messages[messages.length - 1];
    const streamFinished =
      streamingLastIdRef.current !== null && last?.id === streamingLastIdRef.current && !last.streaming;
    streamingLastIdRef.current = last?.streaming ? last.id : null;
    // A prepend also grows the count, but must not yank the view to the bottom.
    if (justPrependedRef.current) {
      justPrependedRef.current = false;
      return;
    }

    const el = scrollRef.current;
    if (!el) return;

    // First batch of history (~100 rows) may land before this list mounts, so
    // `grew` is false even though we've never positioned the view. Jump to the
    // bottom instantly the first time we have content (instant, not smooth — no
    // animated glide over the whole transcript), and re-anchor on the next frame
    // once late layout settles (e.g. base64 image rows finish sizing).
    if (!didInitialScrollRef.current && messages.length > 0) {
      didInitialScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => {
        const e = scrollRef.current;
        if (e) e.scrollTop = e.scrollHeight;
      });
      return;
    }

    if (grew || streamFinished || typing || statusLabel) {
      // Scroll the chat container itself, not via scrollIntoView — that walks up
      // and scrolls every scrollable ancestor (the page), yanking the whole view
      // instead of just the conversation. Setting scrollTop is scoped to el.
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, typing, statusLabel]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        background: 'var(--card)',
      }}
    >
      {(loadingMoreHistory || hasMoreHistory) && (
        <div
          style={{
            alignSelf: 'center',
            fontSize: 12,
            color: 'var(--muted-foreground)',
            padding: '4px 0',
          }}
        >
          {loadingMoreHistory ? 'Cargando mensajes anteriores…' : ''}
        </div>
      )}

      {messages.length === 0 && (
        <div style={{ color: 'var(--muted-foreground)', fontSize: 13, textAlign: 'center', marginTop: 24 }}>
          Pregúntame sobre tus pacientes, planes, recordatorios…
        </div>
      )}

      {blocks.map((block) => {
        // A run of proposal cards (one or more days of the same item folded into
        // day-groups), rendered on the assistant side.
        if (block.kind === 'proposals') {
          return (
            <div
              key={block.key}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <div style={{ width: '100%' }}>
                {block.units.map((unit, ui) => {
                  const groupIds = bulkGroups.get(`${block.key}:${ui}`);
                  return (
                    <React.Fragment key={unit.key}>
                      {unit.grouped ? (
                        <ProposalDayGroupCard
                          proposals={unit.proposals}
                          onResolve={onResolveProposal}
                          onResolveMany={onResolveManyProposals}
                        />
                      ) : (
                        <ProposalCard
                          proposal={unit.proposals[0]}
                          onConfirm={() =>
                            unit.proposals[0].id && onResolveProposal(unit.proposals[0].id, 'accept')
                          }
                          onDiscard={() =>
                            unit.proposals[0].id && onResolveProposal(unit.proposals[0].id, 'reject')
                          }
                        />
                      )}
                      {groupIds && (
                        <BulkProposalActions
                          count={groupIds.length}
                          onAcceptAll={() => onResolveManyProposals(groupIds, 'accept')}
                          onDiscardAll={() => onResolveManyProposals(groupIds, 'reject')}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        }

        const m = block.message;
        // System notices (e.g. connection lost) render as a centered status line.
        if (m.messageType === 'system_notice') {
          return (
            <div
              key={block.key}
              style={{
                alignSelf: 'center',
                maxWidth: '90%',
                textAlign: 'center',
                fontSize: 12,
                fontStyle: 'italic',
                color: 'var(--muted-foreground)',
                padding: '2px 8px',
              }}
            >
              {m.text}
            </div>
          );
        }

        const isUser = m.role === 'user';
        return (
          <div key={block.key} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
            {m.fileUrl &&
              (m.fileType === 'application/pdf' ? (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: 12,
                    border: '1px solid var(--border)',
                    background: 'var(--card)',
                    color: 'var(--foreground)',
                    marginBottom: m.text.trim() !== '' ? 6 : 0,
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: 18 }}>📄</span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={m.fileName || 'Documento PDF'}
                  >
                    {m.fileName || 'Documento PDF'}
                  </span>
                </div>
              ) : (
                <img
                  src={m.fileUrl}
                  alt="Imagen adjunta"
                  style={{
                    maxWidth: '85%',
                    maxHeight: 220,
                    borderRadius: 12,
                    border: '1px solid var(--border)',
                    marginBottom: m.text.trim() !== '' ? 6 : 0,
                    objectFit: 'cover',
                  }}
                />
              ))}
            {m.text.trim() !== '' && (
              // Quiet on purpose: Sissi AI's replies as plain text, the
              // clinician's turns in pale teal, so the chat reads as a
              // companion to the page rather than the main thing on screen.
              <div
                style={{
                  maxWidth: isUser ? '85%' : '100%',
                  padding: isUser ? '7px 11px' : 0,
                  borderRadius: 12,
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  background: isUser ? 'var(--primary-subtle)' : 'transparent',
                  color: 'var(--foreground)',
                }}
              >
                {m.text}
              </div>
            )}
            {m.sendState === 'failed' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 4,
                  fontSize: 12,
                  color: 'var(--destructive)',
                }}
              >
                <span>No se pudo enviar.</span>
                <button
                  onClick={() => onRetrySend(m.id)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    color: 'var(--destructive)',
                    fontSize: 12,
                    fontWeight: 700,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Reintentar
                </button>
              </div>
            )}
          </div>
        );
      })}

      {(statusLabel || (typing && !streamingVisible)) && (
        <div style={{ alignSelf: 'flex-start' }}>
          <div
            style={{
              padding: '6px 12px',
              borderRadius: 12,
              background: 'var(--card)',
              border: '1px solid var(--border)',
              color: 'var(--muted-foreground)',
              fontSize: 13,
              fontStyle: 'italic',
            }}
          >
            {statusLabel || 'Escribiendo…'}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;
